import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import usePermissions from '../../hooks/usePermissions';
import { PERMISSIONS } from '../../config/permissions';
import PasswordInput from '../../components/common/PasswordInput';

export default function AdminPaymentGateway() {
  const { canEdit, canAdd, canDelete } = usePermissions();
  const [gateways, setGateways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    gateway: 'paystack', is_active: false, label: '', public_key: '', secret_key: '',
    webhook_secret: '', bank_name: '', account_number: '', account_name: '',
  });

  const fetchData = () => {
    api.get('/admin/payment-gateways/')
      .then(res => setGateways(res.data.results || res.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const resetForm = () => {
    setFormData({ gateway: 'paystack', is_active: false, label: '', public_key: '', secret_key: '', webhook_secret: '', bank_name: '', account_number: '', account_name: '' });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const res = await api.put(`/admin/payment-gateways/${editingId}/`, formData);
        setGateways(gateways.map(g => g.id === editingId ? res.data : g));
        toast.success('Gateway updated!');
      } else {
        const res = await api.post('/admin/payment-gateways/', formData);
        setGateways([...gateways, res.data]);
        toast.success('Gateway added!');
      }
      resetForm();
    } catch { toast.error('Failed to save gateway config.'); }
  };

  const handleEdit = (gw) => {
    setFormData({
      gateway: gw.gateway, is_active: gw.is_active, label: gw.label || '', public_key: gw.public_key || '',
      secret_key: '', webhook_secret: '', bank_name: gw.bank_name || '',
      account_number: gw.account_number || '', account_name: gw.account_name || '',
    });
    setEditingId(gw.id);
    setShowForm(true);
  };

  const toggleActive = async (gw) => {
    try {
      const res = await api.patch(`/admin/payment-gateways/${gw.id}/`, { is_active: !gw.is_active });
      setGateways(gateways.map(g => g.id === gw.id ? res.data : g));
      toast.success(`${gw.gateway_display || gw.gateway} ${!gw.is_active ? 'activated' : 'deactivated'}.`);
    } catch { toast.error('Failed to toggle gateway.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gateway config?')) return;
    try {
      await api.delete(`/admin/payment-gateways/${id}/`);
      setGateways(gateways.filter(g => g.id !== id));
      toast.success('Gateway removed.');
    } catch { toast.error('Delete failed.'); }
  };

  const gatewayIcon = (gw) => {
    const colors = { paystack: 'bg-blue-100 text-blue-700', flutterwave: 'bg-orange-100 text-orange-700',
      remita: 'bg-purple-100 text-purple-700', manual: 'bg-gray-100 text-gray-700' };
    return colors[gw] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Payment Gateway</h1>
        {canAdd(PERMISSIONS.ADD_PAYMENT_GATEWAY) && (
          <button onClick={() => { resetForm(); setShowForm(!showForm); }}
            className="bg-[#0B5D1E] text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-[#062E10] transition">
            {showForm ? 'Cancel' : 'Add Gateway'}
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Gateway</label>
              <select value={formData.gateway} onChange={e => setFormData({ ...formData, gateway: e.target.value })}
                disabled={!!editingId}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none disabled:bg-gray-100">
                <option value="paystack">Paystack</option>
                <option value="flutterwave">Flutterwave</option>
                <option value="remita">Remita</option>
                <option value="manual">Manual / Bank Transfer</option>
              </select>
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="is_active" checked={formData.is_active}
                onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                className="w-4 h-4 text-[#0B5D1E] rounded" />
              <label htmlFor="is_active" className="text-sm font-medium text-gray-700">Active</label>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-500 mb-1">Organisation Label</label>
              <input type="text" value={formData.label}
                onChange={e => setFormData({ ...formData, label: e.target.value })}
                placeholder="e.g. Nigerian Institution of Surveyors-FCT Branch"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
              <p className="text-xs text-gray-400 mt-1">Displayed on the payment popup as the receiver name</p>
            </div>

            {formData.gateway !== 'manual' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Public Key</label>
                  <input type="text" value={formData.public_key}
                    onChange={e => setFormData({ ...formData, public_key: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Secret Key</label>
                  <PasswordInput value={formData.secret_key}
                    onChange={e => setFormData({ ...formData, secret_key: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none"
                    placeholder={editingId ? '(leave blank to keep current)' : ''} />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Webhook Secret</label>
                  <PasswordInput value={formData.webhook_secret}
                    onChange={e => setFormData({ ...formData, webhook_secret: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none"
                    placeholder={editingId ? '(leave blank to keep current)' : ''} />
                </div>
              </>
            )}

            {(formData.gateway === 'manual' || formData.gateway === 'remita') && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Bank Name</label>
                  <input type="text" value={formData.bank_name}
                    onChange={e => setFormData({ ...formData, bank_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Account Number</label>
                  <input type="text" value={formData.account_number}
                    onChange={e => setFormData({ ...formData, account_number: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-500 mb-1">Account Name</label>
                  <input type="text" value={formData.account_name}
                    onChange={e => setFormData({ ...formData, account_name: e.target.value })}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[#0B5D1E] outline-none" />
                </div>
              </>
            )}
          </div>
          <button type="submit" className="mt-4 bg-[#0B5D1E] text-white px-6 py-2 rounded-lg font-semibold">
            {editingId ? 'Update' : 'Save'}
          </button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {loading ? (
          <div className="col-span-2 text-center py-12 text-gray-500">Loading...</div>
        ) : gateways.length === 0 ? (
          <div className="col-span-2 text-center py-12 text-gray-400">No payment gateways configured.</div>
        ) : gateways.map(gw => (
          <div key={gw.id} className="bg-white rounded-xl shadow-sm border p-5">
            <div className="flex justify-between items-center mb-3">
              <div className="flex items-center gap-3">
                <span className={`text-sm px-3 py-1 rounded-full font-semibold capitalize ${gatewayIcon(gw.gateway)}`}>
                  {gw.gateway_display || gw.gateway}
                </span>
                <span className={`w-3 h-3 rounded-full ${gw.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
              </div>
              <div className="flex gap-2">
                {canEdit(PERMISSIONS.EDIT_PAYMENT_GATEWAY) && (
                  <button onClick={() => toggleActive(gw)}
                    className={`text-sm px-3 py-1 rounded ${gw.is_active ? 'text-yellow-600 hover:bg-yellow-50' : 'text-green-600 hover:bg-green-50'}`}>
                    {gw.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
                {canEdit(PERMISSIONS.EDIT_PAYMENT_GATEWAY) && (
                  <button onClick={() => handleEdit(gw)} className="text-blue-600 hover:underline text-sm">Edit</button>
                )}
                {canDelete(PERMISSIONS.DELETE_PAYMENT_GATEWAY) && (
                  <button onClick={() => handleDelete(gw.id)} className="text-red-600 hover:underline text-sm">Delete</button>
                )}
              </div>
            </div>
            {gw.public_key && <p className="text-xs text-gray-500">Public Key: <span className="font-mono">{gw.public_key.substring(0, 20)}...</span></p>}
            {gw.bank_name && <p className="text-xs text-gray-500 mt-1">Bank: {gw.bank_name} — {gw.account_number} ({gw.account_name})</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
